import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { ENDPOINTS } from 'endpoints.js';
import { apiFetch, checkRole, makePath } from 'functions.js';
// GLOBAL COMPONENTS
import Form from 'core/form/Form';
import Card from 'parts/card/Card';
import Button from 'core/tools/Button';
import Sprite from 'core/tools/Sprite';

// MAIN COMPONENT
const IMVInfoBody = ({ imvInfo, inventoryId }) => {
  const {
    trimId = '',
    engineId = '',
    transmissionId = '',
    options_id_list = '',
    trims = [],
    engines = [],
    transmissions = [],
    options = []
  } = imvInfo;

  const [trim, setTrim] = useState({ options: [], optionsId: null, status: null });
  const [engine, setEngine] = useState({ options: [], optionsId: null, status: null });
  const [transmission, setTransmission] = useState({ options: [], optionsId: null, status: null });
  const [listOptions, setListOptions] = useState({ options: [], optionsId: null, status: null });

  useEffect(() => {
    setTrim(formValues(trims, trimId));
    setEngine(formValues(engines, engineId));
    setTransmission(formValues(transmissions, transmissionId));
    setListOptions(formValues(options, options_id_list ? options_id_list.split(',') : [], true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imvInfo]);

  const formValues = (options, id, isChecklist = false) => {
    return {
      options: options
        ? options.map(({ id: value, name: label }) =>
            isChecklist ? { value, label, cols: { sm: 24, md: 12 } } : { value, label }
          )
        : [],
      optionsId: id,
      status: !options.length ? 'N/A' : id.length ? 'Yes' : 'No'
    };
  };

  const hasBeenSelected = result =>
    result === 'N/A' ? (
      <Form.Label className="inline">{result}</Form.Label>
    ) : (
      <Form.Label className={clsx(result === 'Yes' ? 'text-success' : 'text-danger', 'inline')}>
        <Sprite use={result === 'Yes' ? 'checkmark' : 'cancel'} className="d-inline-block" />
        {result}
      </Form.Label>
    );

  const handleTrimSelect = value => setTrim({ ...trim, optionsId: value, status: value ? 'Yes' : 'No' });
  const handleEngineSelect = value => setEngine({ ...engine, optionsId: value, status: value ? 'Yes' : 'No' });
  const handleTransmissionSelect = value =>
    setTransmission({ ...transmission, optionsId: value, status: value ? 'Yes' : 'No' });
  const handleOptionsSelect = event => {
    const getOptions = listOptions.optionsId.some(item => item === event.target.value)
      ? listOptions.optionsId.filter(item => item !== event.target.value)
      : [...listOptions.optionsId, event.target.value];
    setListOptions({
      ...listOptions,
      optionsId: getOptions,
      status: getOptions.length ? 'Yes' : 'No'
    });
  };

  const handleSave = event => {
    event.preventDefault();
    apiFetch({
      method: 'PUT',
      endpoint: makePath(ENDPOINTS.inventory.updateImvInfo, inventoryId),
      params: {
        trimId: trim.optionsId ? trim.optionsId : null,
        engineId: engine.optionsId ? engine.optionsId : null,
        transmissionId: transmission.optionsId ? transmission.optionsId : null,
        optionsIdList: listOptions.optionsId.length ? String(listOptions.optionsId) : null
      },
      loadingMessage: 'Saving IMV Info.',
      successMessage: 'Saved IMV Info.',
      errorMessage: 'Unable to save IMV Info.'
    });
  };
  // RENDER
  return (
    <>
      <Form.Row>
        <Form.Col cols={{ sm: 4 }} className="p-0">
          <Form.Label>Trim Result Selected</Form.Label>
        </Form.Col>
        <Form.Col cols={{ sm: 2 }} className="p-0">
          {hasBeenSelected(trim.status)}
        </Form.Col>
        <Form.Col className="p-0">
          {trim.options.length ? (
            <Form.Select
              placeholder="Select Trim"
              value={trim.optionsId}
              options={trim.options}
              onSelect={handleTrimSelect}
              useFormik={false}
            />
          ) : (
            <Form.Label>No Trim Options Available</Form.Label>
          )}
        </Form.Col>
      </Form.Row>
      <Form.Row>
        <Form.Col cols={{ sm: 4 }} className="p-0">
          <Form.Label>IMV Engine Result</Form.Label>
        </Form.Col>
        <Form.Col cols={{ sm: 2 }} className="p-0">
          {hasBeenSelected(engine.status)}
        </Form.Col>
        <Form.Col className="p-0">
          {engine.options.length ? (
            <Form.Select
              placeholder="Select Engine"
              value={engine.optionsId}
              options={engine.options}
              onSelect={handleEngineSelect}
              useFormik={false}
            />
          ) : (
            <Form.Label>No Engines Available</Form.Label>
          )}
        </Form.Col>
      </Form.Row>
      <Form.Row>
        <Form.Col cols={{ sm: 4 }} className="p-0">
          <Form.Label>IMV Transmission Result</Form.Label>
        </Form.Col>
        <Form.Col cols={{ sm: 2 }} className="p-0">
          {hasBeenSelected(transmission.status)}
        </Form.Col>
        <Form.Col className="p-0">
          {transmission.options.length ? (
            <Form.Select
              placeholder="Select Transmission"
              value={transmission.optionsId}
              options={transmission.options}
              onSelect={handleTransmissionSelect}
              useFormik={false}
            />
          ) : (
            <Form.Label>No Transmissions Available</Form.Label>
          )}
        </Form.Col>
      </Form.Row>
      <Form.Row>
        <Form.Col cols={{ sm: 4 }} className="p-0">
          <Form.Label>Options Selection Completed</Form.Label>
        </Form.Col>
        <Form.Col cols={{ sm: 2 }} className="p-0">
          {hasBeenSelected(listOptions.status)}
        </Form.Col>
        <Form.Col className="p-0">
          {listOptions.options.length ? (
            <Form.Checklist
              value={listOptions.optionsId}
              options={listOptions.options}
              onChange={handleOptionsSelect}
              useFormik={false}
              fullWidth
              cols={{ sm: 24, lg: 14 }}
            />
          ) : (
            <Form.Label>No Options Available</Form.Label>
          )}
        </Form.Col>
      </Form.Row>
      {(trim.options.length || engine.options.length || transmission.options.length || listOptions.options.length) ?
        <Button.Save type="button" label="Save Imv Info" onClick={handleSave} fullWidth={false} />
        : ''
      }
    </>
  );
};

// EXPORT
export default function IMVInfo(props) {
  return !checkRole(['dslAdmin', 'salesManagerPlus']) ? null : <Card title="IMV Info" body={<IMVInfoBody {...props} />} />;
}
